'use client'

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { createClient } from '@/lib/utils/supabase/client'
import { User, AuthChangeEvent, Session, SupabaseClient } from '@supabase/supabase-js'
import { toast } from 'sonner'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  supabase: SupabaseClient
  signOut: () => Promise<void>
  setUser: (user: User | null) => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const supabaseGlobal = createClient()

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    console.log('[AuthContext] useEffect: Running to get initial user and set up auth listener.');
    setIsLoading(true);

    const getInitialUser = async () => {
      try {
        console.log('[AuthContext] Attempting to fetch initial user...');
        const { data: { user: initialUser }, error } = await supabaseGlobal.auth.getUser();

        if (error) {
          if (error.message.includes('Auth session missing') || error.message.includes('No active session')) {
            console.warn('[AuthContext] No active session found on initial load (expected for anonymous users):', error.message);
          } else {
            console.error('[AuthContext] Error fetching initial user:', error.message);
          }
          setUser(null);
        } else {
          console.log('[AuthContext] Initial user fetched successfully:', initialUser);
          setUser(initialUser);
        }
      } catch (e: any) {
        console.error('[AuthContext] Exception during initial user fetch:', e.message);
        setUser(null);
      } finally {
        setIsLoading(false);
        console.log('[AuthContext] Initial user fetch process complete. isLoading set to false.');
      }
    };

    getInitialUser();

    console.log('[AuthContext] Subscribing to onAuthStateChange...');
    const { data: { subscription } } = supabaseGlobal.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        console.log('[AuthContext] onAuthStateChange event:', event, 'Session user:', session?.user ?? null);
        setUser(session?.user ?? null);

        if (event === 'SIGNED_IN') {
          console.log('[AuthContext] User SIGNED_IN');
        }
        if (event === 'SIGNED_OUT') {
          console.log('[AuthContext] User SIGNED_OUT');
        }
        if (event === 'USER_UPDATED') {
          console.log('[AuthContext] User data UPDATED');
        }
        if (event === 'PASSWORD_RECOVERY') {
          console.log('[AuthContext] PASSWORD_RECOVERY event');
        }
        if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] TOKEN_REFRESHED event');
        }
      }
    );

    return () => {
      if (subscription) {
        console.log('[AuthContext] Unsubscribing from onAuthStateChange.');
        subscription.unsubscribe();
      }
    };
  }, []);

  const signOut = async () => {
    console.log('[AuthContext] signOut called.');
    try {
      const { error } = await supabaseGlobal.auth.signOut();
      if (error) {
        console.error('[AuthContext] signOut error:', error.message);
        toast.error(`Sign out failed: ${error.message}`);
      } else {
        console.log('[AuthContext] supabase.auth.signOut() successful.');
        // setUser(null) will be handled by onAuthStateChange
      }
    } catch (e: any) {
      console.error('[AuthContext] Catch block: signOut failed:', e.message);
      toast.error('An unexpected error occurred during sign out.');
    }
  };

  const refreshUser = async () => {
    console.log('[AuthContext] refreshUser called - manually fetching current user');
    try {
      const { data: { user: currentUser }, error } = await supabaseGlobal.auth.getUser();
      if (error) {
        console.error('[AuthContext] refreshUser error:', error.message);
        setUser(null);
      } else {
        console.log('[AuthContext] refreshUser successful:', currentUser);
        setUser(currentUser);
      }
    } catch (e: any) {
      console.error('[AuthContext] refreshUser catch block:', e.message);
      setUser(null);
    }
  };

  useEffect(() => {
    console.log('[AuthContext] State update: user:', user, 'isLoading:', isLoading);
  }, [user, isLoading]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        supabase: supabaseGlobal,
        signOut,
        setUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 